import React from 'react';
import { animated, useChain, useSpring, useSpringRef } from 'react-spring';
import {
    useEventBasicInformationContext,
    useTicketContext,
} from '../../../contexts';
import { Event, FeatureFlags, LegacyConversionBarProps } from '../../../types';
import { ConversionBarComponentProps } from './ConversionBar/ConversionBar';
import './ConversionBarContainer.scss';
import { ConversionBarContent } from './ConversionBarContent/ConversionBarContent';
import { Discounts } from './Discounts';
import { isTicketSelectionExcludedInListings } from './isTicketSelectionExcludedInListings';

export interface ConversionBarContainerProps
    extends ConversionBarComponentProps,
        LegacyConversionBarProps {
    compactCheckoutDisqualifications: Event['compactCheckoutDisqualifications'];
    ticketsInfo: Event['ticketsInfo'];
    salesStatus: Event['salesStatus']['sales_status'];
    isSalesEnded: boolean;
    isSoldOut: boolean;
    childEvents: Event['childEvents'];
    eventId: Event['id'];
    enableReldExpressCheckoutChanges: FeatureFlags['enableReldExpressCheckoutChanges'];
}

export const ConversionBarContainer: React.FC<ConversionBarContainerProps> = (
    props,
) => {
    const { salesStatus } = useEventBasicInformationContext();
    const { ticketData } = useTicketContext();

    const compactCheckoutDisqualifications =
        props?.enableReldExpressCheckoutChanges
            ? ticketData.compactCheckoutDisqualifications
            : props.compactCheckoutDisqualifications;

    const isTicketSelectionExcluded = isTicketSelectionExcludedInListings({
        soldOut: compactCheckoutDisqualifications?.sold_out,
        reservedSeating: compactCheckoutDisqualifications?.reserved_seating,
        moreThanOneTicket:
            compactCheckoutDisqualifications?.more_than_one_ticket,
        isSalesEnded: compactCheckoutDisqualifications?.is_sales_ended,
        isRsvp: compactCheckoutDisqualifications?.is_rsvp,
        isPostponed: compactCheckoutDisqualifications?.is_postponed,
        isCanceled: compactCheckoutDisqualifications?.is_canceled,
        hasWaitlist: compactCheckoutDisqualifications?.has_waitlist,
        hasWaitingRoom: compactCheckoutDisqualifications?.has_waiting_room,
        hasDiscounts: compactCheckoutDisqualifications?.has_discounts,
        hasAddons: compactCheckoutDisqualifications?.has_addons,
        discountCode: compactCheckoutDisqualifications?.discount_code,
        hasDonations: compactCheckoutDisqualifications?.has_donations,
        hasInventoryTiers:
            compactCheckoutDisqualifications?.has_inventory_tiers,
        externalTicketsAreAvailable: props.externalTicketsAreAvailable,
        salesStatus,
    });

    const springRef = useSpringRef();
    const [containerSprings] = useSpring(
        () => ({
            ref: springRef,
            from: { transform: 'translateY(180px)' },
            to: { transform: 'translateY(0)' },
            config: {
                damping: 15,
                tension: 100,
                precision: 0.0001,
            },
        }),
        [],
    );
    const transRef = useSpringRef();
    const [signalSprings] = useSpring(
        () => ({
            ref: transRef,
            from: { transform: 'translateY(32px)' },
            to: { transform: 'translateY(0)' },
            config: {
                damping: 24,
                tension: 256,
            },
        }),
        [],
    );

    const { enableReldExpressCheckoutChanges } = props;

    useChain([springRef, transRef], [0, 1], 1500);

    return (
        <animated.div
            style={containerSprings}
            className="conversion-bar-container"
        >
            <animated.div
                style={signalSprings}
                className="conversion-bar-signal"
            >
                <Discounts />
            </animated.div>
            <div className="conversion-bar-bordered">
                {props.children}
                <ConversionBarContent
                    {...props}
                    isTicketSelectionExcluded={isTicketSelectionExcluded}
                    enableReldExpressCheckoutChanges={
                        enableReldExpressCheckoutChanges
                    }
                    salesStatus={salesStatus}
                />
            </div>
        </animated.div>
    );
};
